import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useForm, { proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import {
  EntityTypeEnum,
  ExpeditionStatusEnum,
  FeaturesEnum,
  LovDTO,
  MassActionExpeditionResponseOutDTO,
  PermissionTypesEnum,
  TruckTypeWithoutIdDTO,
} from '@api/ringil-api.v2';
import * as yup from 'yup';
import useNavigation from '@hooks/useNavigation.tsx';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { CarrierDistributionSelectorData } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import BulkDistributionForm, {
  bulkDistributionValidationShape,
  initBulkDistribution,
} from '@components/ringil3/Features/Expedition/BulkDistribution/BulkDistributionForm.tsx';
import { mapDistributionSelectorToRequest } from '@app/pages/expedition/create/mappers/formToSaveRequest.ts';
import { splitBulkDistributionSelection } from '@app/pages/customer/expedition-list/bulkDistributionEligibility.ts';
import { JobReferrer } from '@components/ringil3/Features/Job/jobReferrer.ts';
import { internalInfoPanelValidationSchema } from '@app/pages/customer/expedition-detail/InternalInfoTab/InternalInfoPanelForm.tsx';
import Section from '@components/obsolete/Section/Section.tsx';
import LabelsControlled from '@components/obsolete/Labels/LabelsControlled.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgGrayInfo from '@components/ringil3/icons/GrayInfo.tsx';
import { Trans } from 'react-i18next';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import InputFolders from '@components/framework/input/InputFolders/InputFolders.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { InputText } from 'primereact/inputtext';
import RingilTextArea from '@components/framework/input/RingilTextArea.tsx';

interface ExpeditionInfo {
  applicationId: string;
  expeditionId: string;
  /** Needed by "Poptat dopravce": only NEW shipments can be tendered, the rest is listed and skipped. */
  expeditionStatus?: ExpeditionStatusEnum;
  isManualExpedition?: boolean;
}

interface ComponentProps {
  onComplete: () => void;
  selectedExpeditions: ExpeditionInfo[];
}

interface Form {
  overwriteLabels: boolean;
  labels: string[];
  overwriteArchiveExpeditions: boolean;
  archiveLinkedSN?: boolean;
  overwriteFolder: boolean;
  folders?: LovDTO[];
  overwriteFinish: boolean;
  finishExpeditions?: boolean;
  overwriteCarrierNote: boolean;
  carrierNote?: string;
  overwriteTruckType: boolean;
  truckTypeCode?: string;
  overwritePairingSymbol: boolean;
  pairingSymbol?: string;
  /** RD-2922: tender carriers on every selected NEW shipment via an async job (incompatible with archive / finish). */
  tenderCarriers: boolean;
  distribution?: CarrierDistributionSelectorData;
}

export const useMAExp = () => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const MassActionExpedition: React.FC<ComponentProps> = ({ selectedExpeditions, onComplete }) => {
    const { tr } = useTranslationLgs();
    const { expUpdateMassAction, getMassActionExpeditionData, lovTruckTypes, createExpeditionBulkDistributionJob } = useContext(ApiContext);
    const { loggedUser, hasFeature } = useContext(AuthenticatedUserContext);
    const nav = useNavigation();
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [expeditionInfos, setExpeditionInfos] = useState<MassActionExpeditionResponseOutDTO>();
    const [truckTypes, setTruckTypes] = useState<TruckTypeWithoutIdDTO[]>([]);

    const tenderFlag = useSecuredFlag('mass-action-tender-carriers', {
      kind: 'action',
      description: 'Bulk carrier tendering via jobs center (same permission as the per-shipment "Quote carrier")',
      permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW],
    });
    const { eligible: tenderable } = splitBulkDistributionSelection(selectedExpeditions.map(e => ({ ...e, id: e.expeditionId })));

    // The distribution rules apply only while the "Poptat dopravce" toggle is on.
    const validationSchema = internalInfoPanelValidationSchema.shape({
      distribution: yup.object().when('tenderCarriers', {
        is: true,
        then: yup.object().shape(bulkDistributionValidationShape(tr, hasFeature([FeaturesEnum.LADEN_FACTOR]))),
        otherwise: yup.object().nullable(),
      }),
    });

    const form = useForm<Form>(
      validationSchema,
      {
        labels: [],
        overwriteLabels: false,
        overwriteArchiveExpeditions: false,
        archiveLinkedSN: false,
        overwriteFolder: false,
        folders: [],
        overwriteFinish: false,
        finishExpeditions: false,
        overwriteCarrierNote: false,
        carrierNote: undefined,
        overwriteTruckType: false,
        truckTypeCode: undefined,
        overwritePairingSymbol: false,
        pairingSymbol: undefined,
        tenderCarriers: false,
        distribution: initBulkDistribution(loggedUser, hasFeature([FeaturesEnum.CURRENCY_LOCK])),
      },
      d => {
        save(d);
      },
      // Validate on submit only: with the distribution rules in the schema, validating on every change
      // would raise the "errors on the page" toast each time a field is touched before the carrier is picked.
      false,
      false,
    );

    const hasSyncChanges = (data: Form) =>
      data.overwriteLabels ||
      data.overwriteArchiveExpeditions ||
      data.overwriteFolder ||
      data.overwriteFinish ||
      data.overwriteCarrierNote ||
      data.overwriteTruckType ||
      data.overwritePairingSymbol;

    /**
     * Save = the synchronous mass action first (when any of its toggles is on), then the tendering job.
     * The job is created last so a failed mass action never leaves a draft behind, and on success the
     * user lands on the draft page, which is where the next step (removing problem rows) happens.
     */
    const save = (data: Form) => {
      setLoading(true);
      if (data.tenderCarriers && !hasSyncChanges(data)) {
        createTenderJob(data);
      } else {
        // Unchanged legacy path: the mass action is sent even with every toggle off (a no-op on the BE).
        saveMassAction(data, () => (data.tenderCarriers ? createTenderJob(data) : finish()));
      }
    };

    const finish = () => {
      setVisible(false);
      onComplete();
      setLoading(false);
    };

    const createTenderJob = (data: Form) => {
      // No pickup timezone: API carriers are excluded here, so there is no DHL pickup time to place.
      const distribution = mapDistributionSelectorToRequest(data.distribution, loggedUser.defaultTimezoneId);
      if (distribution == null || tenderable.length === 0) {
        setLoading(false);
        return;
      }
      createExpeditionBulkDistributionJob(
        { expeditionIds: tenderable.map(e => e.id), distribution },
        d => {
          finish();
          nav.navigate(nav.urlFunctions.createJobDetail(d.jobId, JobReferrer.EXPEDITIONS));
        },
        null,
        {
          successToast: false,
          onValidationFailed: errors => {
            setLoading(false);
            setBeValidationErrors(errors);
          },
          onError: (_error, showStandardError) => {
            showStandardError();
            setLoading(false);
          },
        },
      );
    };

    const saveMassAction = (data: Form, onSaved: () => void) => {
      expUpdateMassAction(
        {
          expIds: selectedExpeditions.map(t => t.expeditionId),
          internalData: {
            overwriteLabels: data.overwriteLabels,
            labels: data.labels,
            overwriteArchiveExpeditions: data.overwriteArchiveExpeditions,
            archiveLinkedSN: data.archiveLinkedSN,
            overwriteFolder: data.overwriteFolder,
            folderIds: data.folders?.map(f => f.id) ?? [],
            overwriteFinish: data.overwriteFinish,
            finishExpeditions: data.finishExpeditions,
            overwriteCarrierNote: data.overwriteCarrierNote,
            carrierNote: data.overwriteCarrierNote ? data.carrierNote : undefined,
            overwriteTruckType: data.overwriteTruckType,
            truckTypeCode: data.truckTypeCode,
            overwritePairingSymbol: data.overwritePairingSymbol,
            pairingSymbol: data.overwritePairingSymbol ? data.pairingSymbol : undefined,
          },
        },
        onSaved,
        null,
        {
          onError: (_error, showStandardError) => {
            showStandardError();
            setLoading(false);
          },
        },
      );
    };

    const names = proxiedPropertiesOf<Form>();

    useEffect(() => {
      if (!visible) return;
      lovTruckTypes(d => setTruckTypes(d));
    }, [visible]);

    useEffect(() => {
      if (!visible) return;
      const expIds = selectedExpeditions.map(t => t.expeditionId);
      if (expIds && expIds.length > 0) {
        getMassActionExpeditionData({ expIds: expIds }, d => {
          setExpeditionInfos(d);
        });
      }
    }, [visible, selectedExpeditions]);

    return (
      <>
        <RingilSidebar
          visible={visible}
          loading={loading}
          heading={tr(`MAExp.massAction`, `Mass action`)}
          onBack={() => setVisible(false)}
          onContinue={() => {
            form.validateAndSend();
          }}
          disableSaveButton={form.values.tenderCarriers && tenderable.length === 0}
        >
          <SidebarContent>
            <BackendValidationComponent />
            <Section
              label={tr(`MultiEditSupplierNotificationSidebar.archiveExpeditions`, `Archivovat přepravu`)}
              showToggle={true}
              isDisabled={form.values.tenderCarriers}
              onVisibilityChanged={v => {
                form.setFieldValue(names.archiveLinkedSN, false);
                form.setFieldValue(names.overwriteArchiveExpeditions, v);
              }}
            >
              <COInlineMessage icon={<SvgGrayInfo />} variant={'info'}>
                <Trans i18nKey={'MultiEditSupplierNotificationSidebar.archiveExpeditionsInfo'}></Trans>
              </COInlineMessage>
              {expeditionInfos?.allSNCoords?.length > 0 && (
                <InputCheckbox
                  label={tr('MAExp.archivovatIPropojeneAvizace', 'Archivovat i propojené avizace')}
                  value={form.values.archiveLinkedSN}
                  note={expeditionInfos?.allSNCoords.map(t => t.appId).join(', ')}
                  onChange={(_, v) => {
                    form.useProduce(d => {
                      d.archiveLinkedSN = v;
                    });
                  }}
                />
              )}
            </Section>
            <Section
              label={tr(`MultiEditSupplierNotificationSidebar.overrideLabels`, `Override labels`)}
              showToggle={true}
              onVisibilityChanged={v => {
                form.setFieldValue(names.labels, []);
                form.setFieldValue(names.overwriteLabels, v);
              }}
            >
              <LabelsControlled
                entityType={EntityTypeEnum.EXPEDITION}
                value={form.values.labels}
                onChange={v => {
                  form.setFieldValue(names.labels, v);
                }}
              />
            </Section>

            <Section
              label={tr(`MAExp.overwriteFolders`, `Přepsat složky`)}
              showToggle={true}
              onVisibilityChanged={v => {
                form.setFieldValue(names.folders, []);
                form.setFieldValue(names.overwriteFolder, v);
              }}
            >
              <Field label={tr('MAExp.vyberteSlozky', 'Vyberte složky (prázdné = odebrat ze všech složek)')}>
                <InputFolders
                  folders={form.values.folders ?? []}
                  onChange={v =>
                    form.useProduce(d => {
                      d.folders = v;
                    })
                  }
                  entityType={EntityTypeEnum.EXPEDITION}
                />
              </Field>
            </Section>
            <Section
              label={tr(`MultiEditSupplierNotificationSidebar.finishExpeditions`, `Finish expedition`)}
              showToggle={true}
              isDisabled={form.values.tenderCarriers}
              onVisibilityChanged={v => {
                form.setFieldValue(names.finishExpeditions, v);
                form.setFieldValue(names.overwriteFinish, v);
              }}
            ></Section>
            <Section
              label={tr(`MAExp.carrierNote`, `Carrier note`)}
              showToggle={true}
              onVisibilityChanged={v => {
                form.setFieldValue(names.overwriteCarrierNote, v);
                if (!v) form.setFieldValue(names.carrierNote, undefined);
              }}
            >
              <Field label={tr('MAExp.carrierNoteValue', 'Note for carrier')}>
                <RingilTextArea value={form.values.carrierNote ?? ''} onChange={v => form.setFieldValue(names.carrierNote, v)} />
              </Field>
            </Section>
            <Section
              label={tr(`MAExp.truckType`, `Truck type`)}
              showToggle={true}
              onVisibilityChanged={v => {
                form.setFieldValue(names.overwriteTruckType, v);
                if (!v) form.setFieldValue(names.truckTypeCode, undefined);
              }}
            >
              <Field label={tr('MAExp.truckTypeValue', 'Select truck type')}>
                <Dropdown
                  options={truckTypes}
                  value={form.values.truckTypeCode}
                  optionLabel={'name'}
                  optionValue={'code'}
                  onChange={e => form.setFieldValue(names.truckTypeCode, e.value)}
                  placeholder={tr('MAExp.selectTruckType', 'Select truck type')}
                />
              </Field>
            </Section>
            <Section
              label={tr(`MAExp.pairingSymbol`, `Pairing symbol`)}
              showToggle={true}
              onVisibilityChanged={v => {
                form.setFieldValue(names.overwritePairingSymbol, v);
                if (!v) form.setFieldValue(names.pairingSymbol, undefined);
              }}
            >
              <Field label={tr('MAExp.pairingSymbolValue', 'Pairing symbol')}>
                <InputText value={form.values.pairingSymbol ?? ''} onChange={e => form.setFieldValue(names.pairingSymbol, e.target.value)} />
              </Field>
            </Section>
            {/* A tendered shipment is open for carriers — archiving or finishing it in the same go makes no sense,
                so the two pairs of toggles lock each other out. */}
            {tenderFlag.granted && (
              <Section
                label={tr(`MAExp.tenderCarriers`, `Poptat dopravce`)}
                showToggle={true}
                isDisabled={form.values.overwriteArchiveExpeditions || form.values.overwriteFinish}
                onVisibilityChanged={v => {
                  form.setFieldValue(names.tenderCarriers, v);
                  if (!v) form.setFieldValue(names.distribution, initBulkDistribution(loggedUser, hasFeature([FeaturesEnum.CURRENCY_LOCK])));
                }}
              >
                <BulkDistributionForm
                  expeditions={selectedExpeditions.map(e => ({
                    id: e.expeditionId,
                    applicationId: e.applicationId,
                    expeditionStatus: e.expeditionStatus,
                    isManualExpedition: e.isManualExpedition,
                  }))}
                  distribution={form.values.distribution}
                  onChange={v => form.setFieldValue(names.distribution, v)}
                  validationErrors={form.validationErrors.filter(e => e.path?.startsWith('distribution')).map(e => ({ path: e.path || '', message: e.message }))}
                />
              </Section>
            )}
          </SidebarContent>
        </RingilSidebar>
      </>
    );
  };

  return { MassActionExpedition, setVisible };
};

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
`;
